import React, { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../../supabase';
import Modal from '../../../../components/Modal';
import CorFormFields from '../CorFormFields';
import {
  CorFormData,
  CorFormErrors,
  emptyCorForm,
  validateCor,
  buildCreateParams,
} from '../../config';

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface AddCorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCorCreated: () => void;
  teamMembers: TeamMember[];
}

const AddCorModal: React.FC<AddCorModalProps> = ({
  isOpen,
  onClose,
  onCorCreated,
  teamMembers,
}) => {
  const [formData, setFormData] = useState<CorFormData>(emptyCorForm());
  const [errors, setErrors] = useState<CorFormErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(emptyCorForm());
      setErrors({});
      setGeneralError(null);
    }
  }, [isOpen]);

  const handleChange = useCallback(<K extends keyof CorFormData>(field: K, value: CorFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => (prev[field as keyof CorFormErrors] ? { ...prev, [field]: undefined } : prev));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validateCor(formData);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setGeneralError('Please complete the required fields before creating the item.');
      return;
    }

    setIsSubmitting(true);
    setGeneralError(null);

    try {
      const { data, error } = await supabase.rpc('create_cor_item', buildCreateParams(formData));

      if (error) throw error;
      if (data && data.success === false) {
        throw new Error(data.error || 'Failed to create COR item');
      }

      onCorCreated();
      onClose();
    } catch (err) {
      console.error('Error creating COR item:', err);
      setGeneralError(err instanceof Error ? err.message : 'An error occurred while creating the item');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, onCorCreated, onClose]);

  const modalFooter = (
    <div className="modal-footer-actions">
      <button type="button" onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button type="submit" form="add-cor-form" disabled={isSubmitting} className="btn-primary">
        {isSubmitting ? 'Creating...' : 'Create Item'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New COR Item"
      size="md"
      className="cor-modal"
      hasFooter={true}
      footerContent={modalFooter}
    >
      <div className="form-content">
        <form id="add-cor-form" onSubmit={handleSubmit}>
          {generalError && <div className="error-message general-error">{generalError}</div>}
          <CorFormFields
            formData={formData}
            onChange={handleChange}
            teamMembers={teamMembers}
            errors={errors}
            required
          />
        </form>
      </div>
    </Modal>
  );
};

export default AddCorModal;
